import IndividualStageAdvisor from './individual-stage';
import { getOpampNames } from '../gestures/optimization';
import IndexModel from '../index';
import ComponentSelectionInput from '../inputs/component-selection';
import SliderInput from '../inputs/component-selection/slider';
import IndividualStageInput from '../inputs/component-selection/individual-stage';
import ComponentSelectionResults from '../results/component-selection';
import SallenKeyLowPassIdealOpAmp from '../implementation/sallen-key/low-pass/ideal-op-amp';
import SallenKeyLowPassCircuit from '../implementation/sallen-key/low-pass/circuit';
import SecondOrderActualStage from '../stages/actual-second-order';
import { type StageInput } from './individual-stage';
import FilterConstants from '../../filter-constants';
import { isNullOrUndefined } from '../../utils/common';
import { getStageInputsInImplementationOrder } from '../utils/stage';
import { evaluateOpAmps, getAppropriateOpAmps } from '@ebiz/aditools-filter/recommendations';
import { set } from '@ember/object';

export const lockInputsErrorsList = [
  'invalidField',
  'emptyField',
  'voltageSpanLessThanMinimum',
  'voltageSpanGreaterThanMax',
  'vsMinusShouldBeNegative',
  'vsPlusShouldBePositive',
  'frequencyTooHigh',
  'frequencyTooLow',
];

const _defaults = {
  vsPlus: 5,
  vsMinus: -5,
  optimization: FilterConstants.optimizationType.power,
  optimizationPreference: FilterConstants.optimizationPreferenceOptions.nonSpecific,
  compensateForGbw: false,
};

const componentResultErrors = [
  'noOpAmpFoundForRequirements',
  'noOpAmpFoundForRequirementsWithVoltage',
  'frequencyTooHigh',
  'frequencyTooLow',
];

interface OverallAllowedLimits {
  gbw: number;
  rOutOverGBW: number;
  iBias: number;
  slewRate: number;
  specialGain: number;
}

interface StageLimits {
  allowedLimits: SallenKeyLowPassIdealOpAmp;
  powerSpan: number;
  recommendedLimits: SallenKeyLowPassIdealOpAmp;
}

interface OptimizationValues {
  gainValues: SliderInput[];
  sliderValues: SliderInput[];
  sortOrder: number[];
}

interface SliderValues {
  gainValues: SliderInput[];
  sliderValues: SliderInput[];
}

interface OpAmpsFromOptimization {
  sortOrder: number[];
  opAmps: string[];
}

////////////////////////////////////////////////////////////////////////////////////
// component-selection - Advisor that runs when user goes into the components tab //
////////////////////////////////////////////////////////////////////////////////////

export default class ComponentSelectionAdvisor {
  model: IndexModel;
  stageAdvisor: IndividualStageAdvisor;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////
  constructor(model: IndexModel) {
    this.model = model;
    this.stageAdvisor = new IndividualStageAdvisor(this.model);
  }

  ////////////////////////////////////////////////////////////////////////////////
  // User Functions                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  // Creates the component selection input model
  // Inputs:
  //   - data : input model values, if this is null, defaults are used
  // Returns: component selection input model
  generate(data: ComponentSelectionInput): ComponentSelectionInput {
    return this.restructure(new ComponentSelectionInput(data ? data : _defaults));
  }

  // Creates a Copy of the Model
  // Inputs:
  //   - sourceModel : model to be copied from
  // Returns: result - a newly created input model with values copied from the source model
  clone(sourceModel: ComponentSelectionInput): ComponentSelectionInput {
    const result = new ComponentSelectionInput();
    result.preferredAlgorithms = sourceModel.preferredAlgorithms;
    result.choiceOptions = sourceModel.choiceOptions;
    result.compensateForGbw = sourceModel.compensateForGbw;
    result.optimization = sourceModel.optimization;
    result.optimizationPreference = sourceModel.optimizationPreference;
    result.vsMinus = sourceModel.vsMinus;
    result.vsPlus = sourceModel.vsPlus;
    result.individualStageInputs = sourceModel.individualStageInputs
      ? sourceModel.individualStageInputs.map((item: StageInput) => {
          return this.stageAdvisor.clone(item);
        })
      : sourceModel.individualStageInputs;
    result.sortOrder = sourceModel.sortOrder;

    return result;
  }

  // Rebuilds the component selection input model
  // Inputs:
  //   - inputModel : the values of the component selection input model
  // Returns: result - component selection input model. Includes the individual stage inputs as well
  restructure(inputModel: ComponentSelectionInput): ComponentSelectionInput {
    const workingModel = this.clone(inputModel);
    let isValidCircuit = true;

    // 1. Check if the changes in specifications input have the number of stages generated
    const targetStages = this.model.results.specifications.targetStagesWithDistributedGain;
    let currentStages = workingModel.individualStageInputs
      ? workingModel.individualStageInputs.map((item: StageInput) => {
          return this.stageAdvisor.clone(item);
        })
      : workingModel.individualStageInputs;
    if (currentStages === null) {
      currentStages = [];
    }
    if (workingModel.sortOrder.length !== targetStages.length) {
      workingModel.sortOrder = [];
    }
    if (targetStages.length > currentStages.length) {
      workingModel.sortOrder = [];
    } else if (targetStages.length < currentStages.length) {
      currentStages = currentStages.slice(0, targetStages.length - currentStages.length);
      workingModel.sortOrder = [];
    }

    // 2. Build a Temporary Index Model. We do not want to use our main model as a scratch pad
    const indexModel = new IndexModel(this.model.opAmpSource);
    indexModel.inputs.filterType = this.model.inputs.filterType;
    indexModel.inputs.specifications = this.model.inputs.specifications;
    // 2a. Rebuild Individual Stage Inputs (Happens when the changes you make change the stage count and implementation)
    workingModel.individualStageInputs = []; // Clear before Rebuilding
    const optimizationPreference = workingModel.optimizationPreference;

    const newIndividualStages = this.rebuildIndividualStages(targetStages, currentStages, optimizationPreference);

    isValidCircuit = newIndividualStages.isValidCircuit;
    workingModel.individualStageInputs = newIndividualStages.newStages;
    workingModel.sortOrder =
      optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific
        ? []
        : workingModel.sortOrder;
    if (optimizationPreference === FilterConstants.optimizationPreferenceOptions.specific)
      workingModel.optimization = FilterConstants.optimizationType.recommendedSpecific;
    const compensateForGbw = workingModel.compensateForGbw;
    workingModel.compensateForGbw = false; // Getting the Parts needs the Compensate for GBW to be turned off.
    indexModel.inputs.components = workingModel;

    if (isValidCircuit) {
      this.getIndividualStageOpAmps(indexModel, workingModel, inputModel.individualStageInputs);
    }

    // 5. Get the Component Inputs Model and clear the scratchpad Index Model
    set(workingModel, 'compensateForGbw', compensateForGbw);
    const updatedModel = this.clone(workingModel);
    // indexModel = null;

    // 6. Update the sliders
    const sliderValues = this.getSliderValues(updatedModel);
    for (let i = 0; i < updatedModel.individualStageInputs.length; i++) {
      if (sliderValues.sliderValues[i] && sliderValues.gainValues[i]) {
        const componentSizing = sliderValues.sliderValues[i] as SliderInput,
          gainRatio = sliderValues.gainValues[i] as SliderInput;
        updatedModel.individualStageInputs[i].componentSizing.minimum = componentSizing.minimum;
        updatedModel.individualStageInputs[i].componentSizing.maximum = componentSizing.maximum;
        updatedModel.individualStageInputs[i].gainRatio.maximum = gainRatio.maximum;
        updatedModel.individualStageInputs[i].gainRatio.minimum = gainRatio.minimum;
      }
    }

    updatedModel.preferredAlgorithms = inputModel.preferredAlgorithms
      ? inputModel.preferredAlgorithms
      : FilterConstants.model.inputs.components.defaultAlgorithm;
    return updatedModel;
  }

  rebuildIndividualStages(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    targetStages: any,
    currentStages: StageInput[],
    optimizationPreference: string,
  ): { newStages: IndividualStageInput[]; isValidCircuit: boolean } {
    let isValidCircuit = true;
    const newStages = [];
    for (let i = 0; i < targetStages.length; i++) {
      let stageItem = null;
      const currentStage = currentStages[i];
      if (!currentStage) {
        stageItem = this.stageAdvisor.generate(targetStages[i], null, null, null, null);
      } else {
        const isOldDesign = currentStage.componentSizing.value === null;
        const legacySizingSliderValue = isOldDesign
          ? Number(currentStage.legacySizingSliderValue) / FilterConstants.oldSliderSteps
          : null;
        const legacyGainSliderValue = isOldDesign
          ? Number(currentStage.legacyGainSliderValue) / FilterConstants.oldSliderSteps
          : null;
        stageItem = this.stageAdvisor.generate(
          targetStages[i],
          null,
          isOldDesign,
          legacySizingSliderValue,
          legacyGainSliderValue,
        );
        stageItem.opAmp = currentStage.opAmp;

        if (isOldDesign) {
          currentStage.componentSizing.value = stageItem.componentSizing.value;
          currentStage.gainRatio.value = stageItem.gainRatio.value;
        }

        if (optimizationPreference === FilterConstants.optimizationPreferenceOptions.specific) {
          if (currentStage.implementation) {
            const currImplementationSelection = currentStage.implementationSelection.selections;
            const defaultImplementationSelection = stageItem.implementationSelection.selections;
            let doUpdateImplementation = currImplementationSelection.length === defaultImplementationSelection.length;
            if (doUpdateImplementation) {
              for (let j = 0; j < currImplementationSelection.length; j++) {
                const selectionItem = currImplementationSelection[j];
                if (!defaultImplementationSelection.includes(selectionItem)) {
                  doUpdateImplementation = false;
                  break;
                }
              }
            }

            if (doUpdateImplementation) {
              doUpdateImplementation = defaultImplementationSelection.includes(
                currentStage.implementationSelection.selectedItem,
              );
            }

            if (doUpdateImplementation) {
              stageItem.implementationSelection = currentStage.implementationSelection;
              stageItem.implementation = currentStage.implementation;
            }
          }

          const stageDefaults = this.stageAdvisor.getImplementationDefaults(
            targetStages[i],
            stageItem.implementation,
            currentStage,
          );

          stageItem.componentSizing = stageDefaults.componentSizing;
          stageItem.gainRatio = stageDefaults.gainRatio;

          // Component Sizing and Gain Ratio Minimum and Maximum Values are not saved in a design, so when it sees that the values
          // are set to -1 from the design, the values will be set to the defaults based on the target stage values and the implementation
          if (currentStage.componentSizing.minimum === -1 && currentStage.componentSizing.maximum === -1) {
            currentStage.componentSizing.minimum = stageDefaults.componentSizing.minimum;
            currentStage.componentSizing.maximum = stageDefaults.componentSizing.maximum;
          }

          if (currentStage.gainRatio.minimum === -1 && currentStage.gainRatio.maximum === -1) {
            currentStage.gainRatio.minimum = stageDefaults.gainRatio.minimum;
            currentStage.gainRatio.maximum = stageDefaults.gainRatio.maximum;
          }
        }

        stageItem.componentSizing.value = currentStage.componentSizing
          ? this.getComponentSizing(currentStage, stageItem)
          : -1;
        stageItem.gainRatio.value = currentStage.gainRatio ? this.getGainRatio(currentStage, stageItem) : -1;
      }

      isValidCircuit =
        isValidCircuit &&
        ((stageItem.componentSizing.enabled && stageItem.componentSizing.value !== -1) ||
          (!stageItem.componentSizing.enabled && stageItem.componentSizing.value === -1)) &&
        ((stageItem.gainRatio.enabled && stageItem.gainRatio.value !== -1) ||
          (!stageItem.gainRatio.enabled && stageItem.gainRatio.value === -1));
      stageItem.stageLetter = targetStages[i].stageLetter;
      newStages.push(stageItem);
    }

    return { newStages: newStages, isValidCircuit: isValidCircuit };
  }

  getComponentSizing(currentStage: StageInput, stageItem: IndividualStageInput): number {
    let componentSizingValue = stageItem.componentSizing.value;
    if (stageItem.componentSizing.enabled) {
      const currentCompSizeValue = currentStage.componentSizing.value;
      if (
        currentCompSizeValue !== -1 &&
        currentStage.componentSizing.maximum !== -1 &&
        currentStage.componentSizing.minimum !== -1
      ) {
        // ARNA-5151: If the current value is lesser than the minimum or greater than the maximum, extend the minimum or maximum instead
        if (currentCompSizeValue < stageItem.componentSizing.minimum) {
          stageItem.componentSizing.minimum = currentStage.componentSizing.value;
        } else if (currentCompSizeValue > stageItem.componentSizing.maximum) {
          stageItem.componentSizing.maximum = currentStage.componentSizing.value;
        }
        componentSizingValue = currentCompSizeValue;
      }
    }
    return componentSizingValue;
  }

  getGainRatio(currentStage: StageInput, stageItem: IndividualStageInput): number {
    let gainRatioValue = stageItem.gainRatio.value;
    if (stageItem.gainRatio.enabled) {
      const currentGainRatioValue = currentStage.gainRatio.value;
      if (
        currentGainRatioValue !== -1 &&
        currentStage.gainRatio.maximum !== -1 &&
        currentStage.gainRatio.minimum !== -1
      ) {
        // ARNA-5151: If the current value is lesser than the minimum or greater than the maximum, extend the minimum or maximum instead
        if (currentGainRatioValue < stageItem.gainRatio.minimum) {
          stageItem.gainRatio.minimum = currentStage.gainRatio.value;
        } else if (currentGainRatioValue > stageItem.gainRatio.maximum) {
          stageItem.gainRatio.maximum = currentStage.gainRatio.value;
        }
        gainRatioValue = currentGainRatioValue;
      }
    }
    return gainRatioValue;
  }

  getIndividualStageOpAmps(
    indexModel: IndexModel,
    workingModel: ComponentSelectionInput,
    oldStages: IndividualStageInput[],
  ): void {
    // 3. Generate the appropriate op amps based on the limits and recommendations on the original inputs.
    // As of this moment, all added op amps are marked with an "ideal" part name
    const individualStageInputs = getStageInputsInImplementationOrder(
      workingModel.individualStageInputs,
      indexModel.results.components.detailsOfCircuits,
    );
    let opAmpSelection = null;
    const modelParams = {
      circuits: indexModel.results.components.circuits,
      individualStageInputs: individualStageInputs,
      vsPlus: workingModel.vsPlus,
      vsMinus: workingModel.vsMinus,
      optimization: workingModel.optimization,
      stopbandFrequency: indexModel.results.components.stopbandFrequency,
    };
    const params = indexModel.opAmpSource.getFilterOpampsParams(modelParams);
    const opAmpsList = getAppropriateOpAmps(
      params.opAmps,
      params.circuits,
      params.vsPlus,
      params.vsMinus,
      params.optimization,
      params.stopbandFrequency,
      params.applyAdditionalCriteria,
    );

    opAmpSelection = opAmpsList.length > 0 ? getOpampNames(opAmpsList, indexModel) : null;
    if (
      workingModel.optimizationPreference === FilterConstants.optimizationPreferenceOptions.specific &&
      !opAmpSelection
    ) {
      set(workingModel, 'optimization', FilterConstants.optimizationType.allowedSpecific);
      opAmpSelection = opAmpsList.length > 0 ? getOpampNames(opAmpsList, indexModel) : null;
    }

    // 4. Supply OpAmp Values for the Individual Stages
    for (let i = 0; i < workingModel.individualStageInputs.length; i++) {
      if (workingModel.individualStageInputs[i]) {
        if (workingModel.optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific) {
          workingModel.individualStageInputs[i].opAmp = opAmpSelection ? opAmpSelection[i] : 'ideal';
        } else {
          const oldStage = oldStages[i];
          const opAmp = workingModel.individualStageInputs[i].opAmp
            ? workingModel.individualStageInputs[i].opAmp
            : oldStage && oldStage.opAmp
              ? oldStage.opAmp
              : null;
          workingModel.individualStageInputs[i].opAmp = opAmp && opAmp.length > 0 ? opAmp : 'ideal';
        }
      }
    }
  }

  // Returns the default values for a stage
  // Inputs:
  //   - index : index of the individual stages array
  // Returns: default value for the individual stage input based from the target stage referenced by the index
  getStageDefault(index: number): IndividualStageInput | null {
    const targetStages = this.model.results.specifications.targetStagesWithDistributedGain;
    return index < targetStages.length ? this.stageAdvisor.generate(targetStages[index], null, null, null, null) : null;
  }

  // Returns the default values for a stage given a specific implementation
  // Inputs:
  //   - index : index of the individual stages array
  //   - implementation : stage implementation
  // Returns: stageDefaults - default values of the individual stage input based on target stage referenced by the index and implementation
  getImplementationDefaults(index: number, implementation: string): IndividualStageInput {
    const targetStages = this.model.results.specifications.targetStagesWithDistributedGain;
    const stageDefaults = this.stageAdvisor.getImplementationDefaults(targetStages[index], implementation);
    return stageDefaults;
  }

  // Gets the allowed limits with the given stage data
  // Inputs:
  //   - resultsModel : component selection results model
  //   - stageCount : number of stages
  // Returns: hash containing the allowed limits for gbw, rOutOverGBW, iBias, slewRate & specialGain
  _getOverallAllowedLimits(resultsModel: ComponentSelectionResults, stageCount: number): OverallAllowedLimits {
    const opAmpGBW = [],
      routGBW = [],
      iBias = [],
      slewRate = [],
      specialGain = [];
    for (let i = 0; i < stageCount; i++) {
      const stageLimits = resultsModel.getStageLimits(i);
      opAmpGBW.push(stageLimits.allowedLimits.gbw);
      routGBW.push(stageLimits.allowedLimits.rOutOverGBW);
      iBias.push(stageLimits.allowedLimits.iBias);
      slewRate.push(stageLimits.allowedLimits.slewRate);
      specialGain.push(stageLimits.allowedLimits.specialGain);
    }

    return {
      gbw: Math.max(...opAmpGBW),
      rOutOverGBW: Math.min(...routGBW),
      iBias: Math.min(...iBias),
      slewRate: Math.max(...slewRate),
      specialGain: Math.min(...specialGain),
    };
  }

  // Validate Component Selection Inputs
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - Array of error messages
  validate(inputModel: ComponentSelectionInput, invalidFields: Array<string>): Array<ValidationError | null> {
    let result: Array<ValidationError | null> = [];
    result.push(this.validateEcosystem(inputModel));
    result.push(this.validateVsPlus(inputModel, invalidFields));
    result.push(this.validateVsMinus(inputModel, invalidFields));
    result.push(this.validateVoltageSpan(inputModel, invalidFields));
    result = result.filter((p) => !!p);
    if (result.length === 0) {
      const errorC1: ValidationError | null = this.validateC1Value(inputModel, invalidFields);
      if (errorC1 !== null) {
        result.push(errorC1);
      } else {
        const errorCompRatio: ValidationError | null = this.validateComponentSizingAndGainRatio(
          inputModel,
          invalidFields,
        );
        if (errorCompRatio !== null) {
          result.push(errorCompRatio);
        } else {
          const errorOpAmp = this.validateOpAmpAvailability(inputModel, invalidFields);
          if (errorOpAmp !== null) result = result.concat(errorOpAmp);
        }
      }
    }
    return result;
  }

  // Validate target stages from specifications results vs. component input stages
  // Checks if inputModel isn't consistent with the outside world
  // Inputs:
  //   - inputModel : Component Selection Input model
  // Returns: result  hash object containing error information for stage info (too many or too few, if any)
  validateEcosystem(inputModel: ComponentSelectionInput): ValidationError | null {
    let result = null;
    const targetStages = this.model.results.specifications.targetStagesWithDistributedGain;
    const stagesLength = inputModel.individualStageInputs ? inputModel.individualStageInputs.length : 0;
    if (stagesLength > targetStages.length) {
      result = {
        tab: FilterConstants.tabs.components,
        fields: ['individualStages'],
        id: 'tooManyStages',
        parameters: { value: targetStages.length },
      };
    } else if (stagesLength < targetStages.length) {
      result = {
        tab: FilterConstants.tabs.components,
        fields: ['individualStages'],
        id: 'tooFewStages',
        parameters: { value: targetStages.length },
      };
    }
    return result;
  }

  // Validates Positive Supply Voltage (Vs+) Value
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for Vs+ (if any)
  validateVsPlus(inputModel: ComponentSelectionInput, invalidFields: Array<string>): ValidationError | null {
    let result = null;
    const vsPlusName = FilterConstants.fields.inputs.components.vsPlus;
    if (invalidFields.indexOf(vsPlusName) === -1) {
      const value = inputModel.vsPlus;
      if (value < 0) {
        result = {
          tab: FilterConstants.tabs.components,
          fields: [vsPlusName],
          id: 'vsPlusShouldBePositive',
          parameters: { value: value },
        };
      }
    }

    return result;
  }

  // Validates Negative Supply Voltage (Vs+) Value
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for Vs- (if any)
  validateVsMinus(inputModel: ComponentSelectionInput, invalidFields: Array<string>): ValidationError | null {
    let result = null;
    const vsMinusName = FilterConstants.fields.inputs.components.vsMinus;
    if (invalidFields.indexOf(vsMinusName) === -1) {
      const value = inputModel.vsMinus;
      if (value > 0) {
        result = {
          tab: FilterConstants.tabs.components,
          fields: [vsMinusName],
          id: 'vsMinusShouldBeNegative',
          parameters: { value: value },
        };
      }
    }

    return result;
  }

  // Validates Voltage Span (difference of Vs+ and Vs-)
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for Voltage Span (if any)
  validateVoltageSpan(inputModel: ComponentSelectionInput, invalidFields: Array<string>): ValidationError | null {
    let result: ValidationError | null = null;
    const vsMinusName = FilterConstants.fields.inputs.components.vsMinus;
    const vsPlusName = FilterConstants.fields.inputs.components.vsPlus;
    if (invalidFields.indexOf(vsMinusName) === -1 && invalidFields.indexOf(vsPlusName) === -1) {
      const vsMinus = inputModel.vsMinus;
      const vsPlus = inputModel.vsPlus;
      const voltageSpan = vsPlus - vsMinus;
      if (voltageSpan < 1.8) {
        result = {
          tab: FilterConstants.tabs.components,
          fields: [vsPlusName, vsMinusName],
          id: 'voltageSpanLessThanMinimum',
          parameters: { vsPlus: vsPlus, vsMinus: vsMinus, voltageSpan: voltageSpan.toString() },
        };
      } else if (voltageSpan > 36) {
        result = {
          tab: FilterConstants.tabs.components,
          fields: [vsPlusName, vsMinusName],
          id: 'voltageSpanGreaterThanMax',
          parameters: { vsPlus: vsPlus, vsMinus: vsMinus, voltageSpan: voltageSpan.toString() },
        };
      }
      if (!result) {
        const indexModel = new IndexModel(this.model.opAmpSource);
        indexModel.inputs.filterType = this.model.inputs.filterType;
        indexModel.inputs.specifications = this.model.inputs.specifications;
        indexModel.inputs.components = inputModel;
        if (indexModel.results.components.needReferenceStage) {
          if (voltageSpan < 2.7) {
            result = {
              tab: FilterConstants.tabs.components,
              fields: [vsPlusName, vsMinusName],
              id: 'referenceVoltageSpanLessThanMinimum',
              parameters: { vsPlus: vsPlus, vsMinus: vsMinus, voltageSpan: voltageSpan.toString() },
            };
          } else if (voltageSpan > 12) {
            result = {
              tab: FilterConstants.tabs.components,
              fields: [vsPlusName, vsMinusName],
              id: 'referenceVoltageSpanGreaterThanMax',
              parameters: { vsPlus: vsPlus, vsMinus: vsMinus, voltageSpan: voltageSpan.toString() },
            };
          }
        }
        // indexModel = null;
      }
    }

    return result;
  }

  // Checks if there is a valid OpAmp available per stage
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - array of hash object containing error information for OpAmp availability (if any)
  validateOpAmpAvailability(
    inputModel: ComponentSelectionInput,
    invalidFields: Array<string>,
  ): Array<ValidationError | null> {
    let result: Array<ValidationError | null> = [];
    let noOpAmps = false;
    const opAmpErrors = [];
    if (invalidFields.length === 0) {
      const vsMinusName = FilterConstants.fields.inputs.components.vsMinus;
      const vsPlusName = FilterConstants.fields.inputs.components.vsPlus;
      const compensateForGbw = inputModel.compensateForGbw;
      const indexModel = new IndexModel(this.model.opAmpSource);
      indexModel.inputs.filterType = this.model.inputs.filterType;
      indexModel.inputs.specifications = this.model.inputs.specifications;
      //inputModel.compensateForGbw = false; // Getting the Parts needs the Compensate for GBW to be turned off.
      set(inputModel, 'compensateForGbw', false);
      indexModel.inputs.components = inputModel;
      const individualStageInputs = getStageInputsInImplementationOrder(
        inputModel.individualStageInputs,
        indexModel.results.components.detailsOfCircuits,
      );
      const optimizationPreference = inputModel.optimizationPreference;
      if (optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific) {
        const modelParams = {
          circuits: indexModel.results.components.circuits,
          individualStageInputs: individualStageInputs,
          vsPlus: inputModel.vsPlus,
          vsMinus: inputModel.vsMinus,
          optimization: inputModel.optimization,
          stopbandFrequency: indexModel.results.components.stopbandFrequency,
        };
        const params = indexModel.opAmpSource.getFilterOpampsParams(modelParams);
        const opAmpsList = getAppropriateOpAmps(
          params.opAmps,
          params.circuits,
          params.vsPlus,
          params.vsMinus,
          params.optimization,
          params.stopbandFrequency,
          params.applyAdditionalCriteria,
        );

        noOpAmps = !(opAmpsList && opAmpsList.length > 0);
      } else {
        for (let i = 0; i < individualStageInputs.length; i++) {
          const opAmp = individualStageInputs[i].opAmp;
          const modelParams = {
            circuits: indexModel.results.components.circuits[i],
            individualStageInputs: individualStageInputs[i],
            vsPlus: inputModel.vsPlus,
            vsMinus: inputModel.vsMinus,
            optimization: inputModel.optimization,
            stopbandFrequency: indexModel.results.components.stopbandFrequency,
          };
          const params = indexModel.opAmpSource.getFilterOpampsParams(modelParams);
          const allowedOpAmps = evaluateOpAmps(
            params.opAmps,
            params.circuits,
            params.vsPlus,
            params.vsMinus,
            params.optimization,
            params.stopbandFrequency,
          )
            ?.filter((opamp) => {
              return opamp.status !== 'notAllowed';
            })
            .map((opamp) => {
              return opamp.partNumber;
            });

          noOpAmps = !(allowedOpAmps && allowedOpAmps.length > 0);
          if (noOpAmps) {
            break;
          } else {
            if (opAmp !== 'ideal') {
              // Check if the selected OpAmp is valid for the filter requirements
              let errorFields: Array<string> = [];
              const vsMinus = inputModel.vsMinus;
              const vsPlus = inputModel.vsPlus;
              let errorId = 'selectedOpAmpIsIncompatible';
              if (vsPlus - vsMinus < 5 || vsPlus - vsMinus > 10) {
                errorId = 'selectedOpAmpIsIncompatibleWithVoltage';
                errorFields = [vsPlusName, vsMinusName];
              }
              errorFields.push('stageOpAmp' + i.toString());
              if (!allowedOpAmps.includes(opAmp)) {
                opAmpErrors.push({
                  tab: FilterConstants.tabs.components,
                  fields: errorFields,
                  id: errorId,
                  parameters: { stage: individualStageInputs[i].stageLetter, opAmp: opAmp },
                });
              }
            } else {
              // No OpAmp selected currently for this stage
              opAmpErrors.push({
                tab: FilterConstants.tabs.components,
                fields: ['stageOpAmp' + i.toString()],
                id: 'selectedOpAmpIsIdeal',
                parameters: { stage: individualStageInputs[i].stageLetter },
              });
            }
          }
        }
      }

      if (noOpAmps) {
        // One or more stages have no opamps available for requirements
        const vsMinus = inputModel.vsMinus;
        const vsPlus = inputModel.vsPlus;
        let errorId = 'noOpAmpFoundForRequirements';
        let errorFields = [];
        for (let i = 0; i < individualStageInputs.length; i++) {
          errorFields.push('stageOpAmp' + i.toString());
        }
        if (vsPlus - vsMinus < 5 || vsPlus - vsMinus > 10) {
          errorId = 'noOpAmpFoundForRequirementsWithVoltage';
          errorFields = errorFields.concat([vsPlusName, vsMinusName]);
        }

        result.push({
          tab: FilterConstants.tabs.components,
          fields: errorFields,
          id: errorId,
          parameters: {},
        });
      } else {
        // If there are OpAmp errors per stage, it will be built here
        if (opAmpErrors.length > 0) result = result.concat(opAmpErrors);
      }

      //inputModel.compensateForGbw = compensateForGbw; // Restore Compensate for GBW value
      set(inputModel, 'compensateForGbw', compensateForGbw);
      // indexModel = null;
    }
    return result;
  }

  // Validates Master Capacitor (C1) value
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for c1 value (if any)
  validateC1Value(inputModel: ComponentSelectionInput, invalidFields: Array<string>): ValidationError | null {
    let result = null;
    if (invalidFields.length === 0) {
      const indexModel = new IndexModel(this.model.opAmpSource);
      indexModel.inputs.filterType = this.model.inputs.filterType;
      indexModel.inputs.specifications = this.model.inputs.specifications;
      indexModel.inputs.components = inputModel;
      let frequency, c1big, c1tiny;
      const stages = indexModel.results.components.detailsOfStages;
      const circuits = indexModel.results.components.detailsOfCircuits;
      for (let i = 0; i < stages.length; i++) {
        const circuitData = circuits[i];
        const stageImplementation = stages[i].implementation;
        switch (stageImplementation) {
          case FilterConstants.implementationType.gain: {
            c1big = FilterConstants.Limits.INFINITY;
            c1tiny = FilterConstants.Limits.INFINITY;
            break;
          }
          case FilterConstants.implementationType.firstOrder:
          case FilterConstants.implementationType.sallenKey:
          case FilterConstants.implementationType.inductor:
          case FilterConstants.implementationType.multipleFeedback:
          case FilterConstants.implementationType.deliyannisFriend: {
            c1big = circuitData.c1.maximum;
            c1tiny = circuitData.c1.minimum;
            frequency = circuitData.circuit.frequency;
            break;
          }
        }
        if (c1big < c1tiny) {
          if (frequency > 1000) {
            result = {
              tab: FilterConstants.tabs.components,
              fields: [],
              id: 'frequencyTooHigh',
              parameters: {},
            };
          } else {
            result = {
              tab: FilterConstants.tabs.components,
              fields: [],
              id: 'frequencyTooLow',
              parameters: {},
            };
          }
          break;
        }
      }

      // indexModel = null;
    }
    return result;
  }

  // Validates Component Sizing and Gain Ratio values
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - invalidFields : array of string containing fields with parse errors
  // Returns: result - hash object containing error information for c1 value (if any)
  validateComponentSizingAndGainRatio(
    inputModel: ComponentSelectionInput,
    invalidFields: Array<string>,
  ): ValidationError | null {
    let result = null;
    if (invalidFields.length === 0) {
      const stages = inputModel.individualStageInputs;
      for (let i = 0; i < stages.length; i++) {
        const componentSizing = stages[i].componentSizing;
        const gainRatio = stages[i].gainRatio;
        if (
          (componentSizing.enabled && componentSizing.value === -1) ||
          (gainRatio.enabled && gainRatio.value === -1)
        ) {
          result = {
            tab: FilterConstants.tabs.components,
            fields: [],
            id: 'frequencyTooHigh',
            parameters: {},
          };
          break;
        }
      }
    }
    return result;
  }

  // Gets the default values for a stage given a specific implementation
  // Inputs:
  //   - stageInputs : working model for specific stage
  //   - index : index of the actual stages array
  // Returns: limits for a particular stage reference by the index
  getStageLimits(stageInputs: IndividualStageInput, vsPlus: number, vsMinus: number, index: number): StageLimits {
    const indexModel = new IndexModel(this.model.opAmpSource);
    indexModel.inputs.filterType = this.model.inputs.filterType;
    indexModel.inputs.specifications = this.model.inputs.specifications;

    const inputModel = this.clone(this.model.inputs.components);
    inputModel.vsPlus = vsPlus;
    inputModel.vsMinus = vsMinus;
    const individualStageInputs = inputModel.individualStageInputs;
    individualStageInputs[index].implementation = stageInputs.implementation;
    individualStageInputs[index].componentSizing = stageInputs.componentSizing;
    individualStageInputs[index].gainRatio = stageInputs.gainRatio;
    indexModel.inputs.components = inputModel;

    const stages = indexModel.results.components;
    return stages.getStageLimits(inputModel.sortOrder.indexOf(index));
  }

  // Gets the overall limits (gbw, rOutOverGBW, iBias, slewRate & specialGain)
  // Returns: overall limits from component selection results model
  getOverallLimits(): OverallAllowedLimits {
    return this.model.results.components.overallLimits;
  }

  // Gets the default values of component sizing and gain depending on optimization
  // Inputs:
  //   - inputModel : Component Selection Input model
  // Returns: hash object containing the sort order, component sizing values and gain values per stage
  getOptimizationValues(inputModel: ComponentSelectionInput): OptimizationValues {
    const workingModel = this.clone(inputModel);
    workingModel.optimizationPreference = FilterConstants.optimizationPreferenceOptions.nonSpecific;
    const indexModel = this._createIndexModel(workingModel);
    const circuits = indexModel.results.components.circuits;
    const sortOrder = indexModel.results.components.actualStages.map((item: SecondOrderActualStage) => {
      return item.stageId ? item.stageId - 1 : null;
    });
    const sliderValues = this._getSizingSliderValues(circuits);
    const gainValues = this._getGainRatios(circuits);
    const sliderValuesSorted = [];
    const gainValuesSorted = [];

    // Circuits received is sort optimized but we need the inputs in stage alphabetical order.
    for (let i = 0; i < sortOrder.length; i++) {
      const index = sortOrder.indexOf(i);
      if (sliderValues[index] && gainValues[index]) {
        sliderValuesSorted.push(sliderValues[index] as SliderInput);
        gainValuesSorted.push(gainValues[index] as SliderInput);
      }
    }

    return { sortOrder: sortOrder, sliderValues: sliderValuesSorted, gainValues: gainValuesSorted };
  }

  // Gets the current values of component sizing and gain regardless of optimization
  // Inputs:
  //   - inputModel : Component Selection Input model
  // Returns: hash object containing the component sizing values and gain values per stage
  getSliderValues(inputModel: ComponentSelectionInput): SliderValues {
    const workingModel = this.clone(inputModel);
    const indexModel = this._createIndexModel(workingModel);
    const circuits = indexModel.results.components.circuits;

    const sliderValues = this._getSizingSliderValues(circuits);
    const gainValues = this._getGainRatios(circuits);

    const sortOrder = indexModel.results.components.actualStages.map((item: SecondOrderActualStage) => {
      return item.stageId ? item.stageId - 1 : null;
    });
    const sliderValuesSorted = [];
    const gainValuesSorted = [];

    // Circuits received is sort optimized but we need the inputs in stage alphabetical order.
    for (let i = 0; i < sortOrder.length; i++) {
      const index = sortOrder.indexOf(i);
      if (sliderValues[index] && gainValues[index]) {
        sliderValuesSorted.push(sliderValues[index] as SliderInput);
        gainValuesSorted.push(gainValues[index] as SliderInput);
      }
    }

    return { sliderValues: sliderValuesSorted, gainValues: gainValuesSorted };
  }

  // Create a working copy of the whole model
  // Used for computing the individual stage inputs
  // Inputs:
  //   - inputModel : Component Selection Input model
  // Returns: indexModel - a copy of the whole model with working component selection inputs
  _createIndexModel(inputModel: ComponentSelectionInput): IndexModel {
    const workingModel = this.clone(inputModel);
    const indexModel = new IndexModel(this.model.opAmpSource);
    indexModel.inputs.filterType = this.model.inputs.filterType;
    indexModel.inputs.specifications = this.model.inputs.specifications;
    indexModel.inputs.components = workingModel;

    return indexModel;
  }

  // Get component sizing slider (min, max, value)
  // Inputs:
  //   - circuits : stage circuit
  // Returns: sliderValues - component sizing slider object
  _getSizingSliderValues(circuits: SallenKeyLowPassCircuit[]): SliderInput[] {
    const sliderValues = circuits.map((item: SallenKeyLowPassCircuit) => {
      return this.stageAdvisor.getComponentSizingValues(item);
    });
    return sliderValues;
  }

  // Get gain slider (min, max, value)
  // Inputs:
  //   - circuits : stage circuit
  // Returns: sliderValues - gain slider object
  _getGainRatios(circuits: SallenKeyLowPassCircuit[]): SliderInput[] {
    const sliderValues = circuits.map((item: SallenKeyLowPassCircuit) => {
      return this.stageAdvisor.getGainRatio(item);
    });
    return sliderValues;
  }

  // Rebuild the OpAmps per stage when the user changes the optimization
  // (Technically it is an array of OpAmps but the values are just the same)
  // Inputs:
  //   - inputModel : Component Selection Input model
  // Returns: hash object containing an empty sortOrder (force to sort to default) and the list of OpAmps per stage
  getOpAmpsFromOptimization(inputModel: ComponentSelectionInput): OpAmpsFromOptimization {
    let opAmpSelection = null;
    let isValidCircuit = true;
    const workingModel = this.clone(inputModel);
    const indexModel = new IndexModel(this.model.opAmpSource);
    indexModel.inputs.filterType = this.model.inputs.filterType;
    indexModel.inputs.specifications = this.model.inputs.specifications;
    workingModel.compensateForGbw = false; // Getting the Parts needs the Compensate for GBW to be turned off. This is a scratchpad model so this is ok.
    workingModel.individualStageInputs = []; // Since we are switching to Pick for Me, we throw away all component sizing, gain and implementation and we rebuild
    const targetStages = indexModel.results.specifications.targetStagesWithDistributedGain;
    const newStages = [];
    for (let i = 0; i < targetStages.length; i++) {
      const stageItem = this.stageAdvisor.generate(targetStages[i], null, null, null, null);
      isValidCircuit =
        isValidCircuit &&
        ((stageItem.componentSizing.enabled && stageItem.componentSizing.value !== -1) ||
          (!stageItem.componentSizing.enabled && stageItem.componentSizing.value === -1)) &&
        ((stageItem.gainRatio.enabled && stageItem.gainRatio.value !== -1) ||
          (!stageItem.gainRatio.enabled && stageItem.gainRatio.value === -1));
      stageItem.stageLetter = targetStages[i].stageLetter;
      newStages.push(stageItem);
    }
    workingModel.individualStageInputs = newStages;
    indexModel.inputs.components = workingModel;
    const individualStageInputs = getStageInputsInImplementationOrder(
      workingModel.individualStageInputs,
      indexModel.results.components.detailsOfCircuits,
    );
    if (isValidCircuit) {
      const modelParams = {
        circuits: indexModel.results.components.circuits,
        individualStageInputs: individualStageInputs,
        vsPlus: workingModel.vsPlus,
        vsMinus: workingModel.vsMinus,
        optimization: workingModel.optimization,
        stopbandFrequency: indexModel.results.components.stopbandFrequency,
      };
      const params = indexModel.opAmpSource.getFilterOpampsParams(modelParams);
      const opAmpsList = getAppropriateOpAmps(
        params.opAmps,
        params.circuits,
        params.vsPlus,
        params.vsMinus,
        params.optimization,
        params.stopbandFrequency,
        params.applyAdditionalCriteria,
      );

      opAmpSelection = opAmpsList.length > 0 ? getOpampNames(opAmpsList, indexModel) : null;
    }
    const retOpAmps = [];
    for (let i = 0; i < individualStageInputs.length; i++) {
      retOpAmps.push(opAmpSelection ? (opAmpSelection[i] as string) : 'ideal');
    }
    // indexModel = null;
    return { sortOrder: [], opAmps: retOpAmps };
  }

  // Checks if there is an error that results in an error or no suitable OpAmp found
  // Inputs:
  //   - inputModel : Component Selection Input model
  //   - errorList : Array list of errors
  // Returns: true if an error is found or if one or more opamps are "ideal", otherwise false
  isComponentResultError(inputModel: ComponentSelectionInput, errorList: ValidationError[]): boolean {
    const errorItem = errorList.find((item: ValidationError) => componentResultErrors.includes(item.id));
    const opAmpIdeal =
      inputModel.individualStageInputs.find((item: IndividualStageInput) => item.opAmp === 'ideal') &&
      errorList.find((item: ValidationError) => item.id !== 'selectedOpAmpIsIdeal');
    return typeof errorItem !== 'undefined' || typeof opAmpIdeal !== 'undefined';
  }

  // Checks if the tab has restrictions that cause it to disable the succeeding steps in the wizard
  // Returns: ideals - true if there is one or more ideal opamps, otherwise false
  hasRestrictions(): boolean {
    const ideals = this.hasIdealOpAmps();
    return ideals;
  }

  // Checks if the one or more of the individual stages has an ideal op amp assigned to it
  // Returns: result - true if there is one of more ideal opamps, otherwise false
  hasIdealOpAmps(): boolean {
    let result = false;
    const data = this.model.inputs.components;
    if (!isNullOrUndefined(data)) {
      const stageInputs = data.individualStageInputs;
      const resultItem = stageInputs.find((stageItem: IndividualStageInput) => {
        return stageItem.opAmp === 'ideal';
      });
      result = typeof resultItem !== 'undefined';
    }
    return result;
  }

  // This function checks if the selected OpAmp (I want to choose) specification falls within
  // the recommended limits of that stage.
  // Inputs:
  //   - stageIndex : index of the actual stages array
  //   - opAmp : OpAmp name for that stage
  isRecommendedOpAmp(
    stageIndex: number,
    opAmp: string,
    stage: IndividualStageInput,
    workingModel: ComponentSelectionInput,
  ): boolean {
    const indexModel = this._createIndexModel(workingModel);
    const modelParams = {
      circuits: indexModel.results.components.circuits[workingModel.sortOrder.indexOf(stageIndex)],
      individualStageInputs: stage,
      vsPlus: indexModel.inputs.components.vsPlus,
      vsMinus: indexModel.inputs.components.vsMinus,
      optimization: indexModel.inputs.components.optimization,
      stopbandFrequency: indexModel.results.components.stopbandFrequency,
    };
    const params = indexModel.opAmpSource.getFilterOpampsParams(modelParams);
    const recommendedOpAmps = evaluateOpAmps(
      params.opAmps,
      params.circuits,
      params.vsPlus,
      params.vsMinus,
      params.optimization,
      params.stopbandFrequency,
    )
      ?.filter((opamp) => {
        return opamp.status === 'recommended';
      })
      .map((opamp) => {
        return opamp.partNumber;
      });

    return recommendedOpAmps.includes(opAmp);
  }
}
